import React, { useState, useEffect } from 'react';
import { Pill, Search, Edit, Trash2, X, Save, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const Refills = () => {
  const [refills, setRefills] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // ✅ Pagination States
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ 
      id: null, patient_name: '', ndc: '', drug_name: '', status: '', 
      quantity: '', days_supply: '', amount: '', expiration_date: '', order_date: '' 
  });

  // Add New Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    patient_id: '',
    rx_no: 'TBD', 
    ndc: '', 
    drug_name: '', 
    quantity: '', 
    days_supply: '', 
    amount: '', 
    expiration_date: '', 
    order_date: new Date().toISOString().split('T')[0],
    prescriber_name: '', prescriber_phone: ''
  });

  // Change this to match your local folder path
const API_URL = 'https://unifyrxcrm.com/AG-BACKEND/index.php';
  // 1. Fetch Refills
  const fetchRefills = async () => {
    try {
      const response = await axios.get(`${API_URL}?action=get_refills`);
      if (Array.isArray(response.data)) setRefills(response.data);
      else setRefills([]);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching refills:", error);
      setLoading(false);
    }
  };

  // 2. Fetch Patients
  const fetchPatients = async () => {
    try {
        const res = await axios.get(`${API_URL}?action=get_patients`);
        if (Array.isArray(res.data)) setPatients(res.data);
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    fetchRefills();
    fetchPatients();
  }, []);

  // Date Formatter
  const formatDate = (dateString) => {
      if (!dateString || dateString === '0000-00-00') return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US');
  };

  // Search Filter
  const filteredRefills = refills.filter(item => 
    (item.full_name && item.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.drug_name && item.drug_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.ndc && item.ndc.includes(searchTerm))
  );

  // ✅ Pagination Logic
  const totalEntries = filteredRefills.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);
  
  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
  const paginatedRefills = filteredRefills.slice(startIndex, endIndex);

  // Reset to page 1 when search or entries change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  // Handle Delete
  const handleDelete = async (id) => {
    if(window.confirm("Are you sure you want to remove this requirement?")) {
        try {
            await axios.post(`${API_URL}?action=delete_prescription`, { id });
            fetchRefills();
        } catch (error) { console.error(error); alert("Error deleting"); }
    }
  };

  // Handle Edit Click
  const handleEdit = (item) => {
    setEditForm({
        id: item.id,
        patient_name: item.full_name,
        ndc: item.ndc || '',
        drug_name: item.drug_name,
        status: item.status || 'Processing',
        quantity: item.quantity,
        days_supply: item.days_supply,
        amount: item.amount,
        expiration_date: item.expiration_date === '0000-00-00' ? '' : (item.expiration_date || ''),
        order_date: item.order_date === '0000-00-00' ? '' : (item.order_date || '')
    });
    setShowEditModal(true);
  };

  // Handle Update Submit
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
        const payload = { ...editForm, amount: editForm.amount || '0.00' };
        await axios.post(`${API_URL}?action=update_prescription`, payload);
        alert("Refill Updated!");
        setShowEditModal(false);
        fetchRefills();
    } catch (error) { console.error(error); alert("Error updating"); }
  };

  // Handle NEW Add Submit
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
        const payload = { 
            ...addForm, 
            strength: '', form: '', 
            auth_ref_number: '', last_ref_number: '', last_fill_date: '',
            status: 'Processing',
            amount: addForm.amount || '0.00'
        };
        await axios.post(`${API_URL}?action=add_prescription`, payload);
        alert("New Refill Requirement Created!");
        setShowAddModal(false);
        setAddForm({ patient_id: '', rx_no: 'TBD', ndc: '', drug_name: '', quantity: '', days_supply: '', amount: '', expiration_date: '', order_date: new Date().toISOString().split('T')[0], prescriber_name: '', prescriber_phone: '' });
        fetchRefills();
    } catch (error) { console.error(error); alert("Error creating refill"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-2">
          <Pill className="text-teal-600" /> Summary Refill Drug Requirement Report
        </h2>

        <div className="flex gap-3">
            <button onClick={() => setShowAddModal(true)} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 flex items-center gap-2 shadow">
                <Plus size={16} /> Add Requirement
            </button>
            <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input type="text" placeholder="Search Patient, Drug, NDC..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-72" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}/>
            </div>
        </div>
      </div>

      {/* ✅ Entries Dropdown */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select 
            value={entriesPerPage} 
            onChange={(e) => setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        
        <div className="text-sm text-gray-600">
          Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 border-b border-black">
            <tr>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">Patient</th>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">NDC</th>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">Drug Name</th>
              <th className="px-6 py-3 text-center font-bold text-gray-900 uppercase">Quantity</th>
              <th className="px-6 py-3 text-center font-bold text-gray-900 uppercase">Days</th>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">Refill Date</th>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">Order Date</th>
              <th className="px-6 py-3 text-center font-bold text-gray-900 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
                <tr><td colSpan="8" className="p-4 text-center">Loading report...</td></tr>
            ) : paginatedRefills.length === 0 ? (
                <tr><td colSpan="8" className="p-6 text-center text-gray-500">No pending refills found.</td></tr>
            ) : (
                paginatedRefills.map((item) => (
                <tr key={item.id} className="hover:bg-teal-50 font-mono border-b">
                    <td className="px-6 py-3 text-teal-600 font-bold">{item.full_name}</td>
                    <td className="px-6 py-3 text-gray-900">{item.ndc || '-'}</td>
                    <td className="px-6 py-3 text-gray-900 font-bold">{item.drug_name}</td>
                    <td className="px-6 py-3 text-center font-medium">{item.quantity}</td>
                    <td className="px-6 py-3 text-center text-gray-600">{item.days_supply || '-'}</td>
                    <td className="px-6 py-3 text-red-600 font-bold">{formatDate(item.expiration_date)}</td>
                    <td className="px-6 py-3 text-gray-600">{formatDate(item.order_date)}</td>
                    
                    <td className="px-6 py-3 flex justify-center gap-2">
                        <button onClick={() => handleEdit(item)} className="text-teal-500 hover:bg-teal-50 p-1 rounded border border-transparent hover:border-teal-200" title="Edit"><Edit size={16}/></button>
                        <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:bg-red-50 p-1 rounded border border-transparent hover:border-red-200" title="Delete"><Trash2 size={16}/></button>
                    </td>
                </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ Pagination Controls */}
      {entriesPerPage !== 'all' && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ChevronLeft size={16} /> Previous
          </button>
          
          <div className="flex gap-1">
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              if (
                pageNum === 1 || 
                pageNum === totalPages || 
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 border rounded ${
                      currentPage === pageNum 
                        ? 'bg-teal-600 text-white border-teal-600' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return <span key={pageNum} className="px-2">...</span>;
              }
              return null;
            })}
          </div>

          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Add Requirement Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-[500px] p-6 relative">
                <button onClick={() => setShowAddModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                <h3 className="text-xl font-bold mb-4 text-teal-700">Add New Refill Requirement</h3>
                <form onSubmit={handleAddSubmit} className="space-y-3 text-sm">
                    <div>
                        <label className="block font-bold text-gray-700 mb-1">Select Patient</label>
                        <select className="w-full border p-2 rounded" required value={addForm.patient_id} onChange={e => setAddForm({...addForm, patient_id: e.target.value})}>
                            <option value="">-- Choose a Patient --</option>
                            {patients.map(p => (<option key={p.id} value={p.id}>{p.full_name}</option>))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4"><input type="text" placeholder="NDC" className="border p-2 rounded" value={addForm.ndc} onChange={e => setAddForm({...addForm, ndc: e.target.value})} /><input type="text" placeholder="Drug Name" className="border p-2 rounded" required value={addForm.drug_name} onChange={e => setAddForm({...addForm, drug_name: e.target.value})} /></div>
                    <div className="grid grid-cols-2 gap-4"><input type="number" placeholder="Qty" className="border p-2 rounded" value={addForm.quantity} onChange={e => setAddForm({...addForm, quantity: e.target.value})} /><input type="number" placeholder="Days" className="border p-2 rounded" value={addForm.days_supply} onChange={e => setAddForm({...addForm, days_supply: e.target.value})} /></div>
                    <div className="grid grid-cols-2 gap-4"><div><label className="text-xs text-gray-500">Refill Date</label><input type="date" className="w-full border p-2 rounded" value={addForm.expiration_date} onChange={e => setAddForm({...addForm, expiration_date: e.target.value})} /></div><div><label className="text-xs text-gray-500">Order Date</label><input type="date" className="w-full border p-2 rounded" value={addForm.order_date} onChange={e => setAddForm({...addForm, order_date: e.target.value})} /></div></div>
                    
                    <button type="submit" className="w-full bg-teal-600 text-white font-bold py-2 rounded hover:bg-teal-700 mt-4">Create Requirement</button>
                </form>
            </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative">
                <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                <h3 className="text-xl font-bold mb-4 text-teal-700">Update Requirement</h3>
                <form onSubmit={handleUpdate} className="space-y-4 text-sm">
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient</label><input type="text" className="w-full border p-2 rounded bg-gray-100 text-gray-600" value={editForm.patient_name} disabled /></div>
                    <div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Drug Name</label><input type="text" className="w-full border p-2 rounded" value={editForm.drug_name} onChange={e => setEditForm({...editForm, drug_name: e.target.value})} /></div><div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">NDC</label><input type="text" className="w-full border p-2 rounded" value={editForm.ndc} onChange={e => setEditForm({...editForm, ndc: e.target.value})} /></div></div>
                    <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label><select className="w-full border p-2 rounded" value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}><option value="Processing">Processing</option><option value="Not Picked">Not Picked</option><option value="Picked">Picked</option><option value="Delivered">Delivered</option></select></div>
                    <div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Qty</label><input type="number" className="w-full border p-2 rounded" value={editForm.quantity} onChange={e => setEditForm({...editForm, quantity: e.target.value})}/></div><div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Days</label><input type="number" className="w-full border p-2 rounded" value={editForm.days_supply} onChange={e => setEditForm({...editForm, days_supply: e.target.value})}/></div></div>
                    <div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Refill Date</label><input type="date" className="w-full border p-2 rounded" value={editForm.expiration_date} onChange={e => setEditForm({...editForm, expiration_date: e.target.value})}/></div><div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Order Date</label><input type="date" className="w-full border p-2 rounded" value={editForm.order_date} onChange={e => setEditForm({...editForm, order_date: e.target.value})}/></div></div>
                    
                    <button type="submit" className="w-full bg-teal-600 text-white font-bold py-2 rounded hover:bg-teal-700 flex items-center justify-center gap-2"><Save size={16} /> Update Record</button>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default Refills;